import { createContext, useContext, useCallback, useMemo, useId, useState, useEffect } from 'react'
import type { ContainerTabItem, ContainerTabsContextValue, ContainerTabsProps } from './types'

const ContainerTabsContext = createContext<ContainerTabsContextValue | null>(null)

/** Hook para acessar o contexto de abas de container. */
// eslint-disable-next-line react-refresh/only-export-components -- hook exportado junto ao provider (padrão de contexto)
export function useContainerTabsContext(): ContainerTabsContextValue {
  const context = useContext(ContainerTabsContext)
  if (!context) {
    throw new Error('useContainerTabsContext must be used within a ContainerTabs provider')
  }
  return context
}

interface ContainerTabsProviderProps extends Pick<ContainerTabsProps, 'defaultTab' | 'value' | 'onChange' | 'id'> {
  children: React.ReactNode
}

export function ContainerTabsProvider({
  children,
  defaultTab,
  value: controlledValue,
  onChange,
  id,
}: ContainerTabsProviderProps) {
  const generatedId = useId()
  const tabsId = id || generatedId
  
  // Estado interno para abas registradas
  const [tabs, setTabs] = useState<ContainerTabItem[]>([])
  
  // Estado interno (não controlado)
  const [internalTab, setInternalTab] = useState(defaultTab || '')
  
  // Valor atual (controlado ou não)
  const activeTab = controlledValue !== undefined ? controlledValue : internalTab
  
  // Registra uma aba
  const registerTab = useCallback((tab: ContainerTabItem) => {
    setTabs((prev) => {
      if (prev.some((t) => t.value === tab.value)) {
        return prev
      }
      return [...prev, tab]
    })
  }, [])
  
  // Remove registro de uma aba
  const unregisterTab = useCallback((value: string) => {
    setTabs((prev) => prev.filter((t) => t.value !== value))
  }, [])
  
  // Define a aba ativa
  const setActiveTab = useCallback(
    (value: string) => {
      if (!controlledValue) {
        setInternalTab(value)
      }
      
      onChange?.(value)
    },
    [controlledValue, onChange]
  )
  
  // Se não há aba ativa e há abas registradas, seleciona a primeira
  useEffect(() => {
    if (!activeTab && tabs.length > 0) {
      const firstEnabledTab = tabs.find((t) => !t.disabled)
      if (firstEnabledTab) {
        const sync = window.setTimeout(() => {
          setActiveTab(firstEnabledTab.value)
        }, 0)
        return () => window.clearTimeout(sync)
      }
    }
  }, [activeTab, tabs, setActiveTab])
  
  const contextValue = useMemo<ContainerTabsContextValue>(
    () => ({
      activeTab,
      setActiveTab,
      tabsId,
      registerTab,
      unregisterTab,
      tabs,
    }),
    [activeTab, setActiveTab, tabsId, registerTab, unregisterTab, tabs]
  )
  
  return <ContainerTabsContext.Provider value={contextValue}>{children}</ContainerTabsContext.Provider>
}
